import { useMemo, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { motion } from 'framer-motion'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'
import {
  integrations,
  protocolLabels,
  categories,
  type Integration,
  type Protocol, integrationSlug } from './integration-data'

const EASE = [0.22, 1, 0.36, 1] as const

const protocolFilters: { key: Protocol | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'saml', label: 'SAML' },
  { key: 'oidc', label: 'OIDC' },
  { key: 'oauth', label: 'OAuth 2' },
  { key: 'radius', label: 'RADIUS' },
  { key: 'browser', label: 'Browser' },
  { key: 'native', label: 'Native' },
]

export default function IntegrationCatalog() {
  const [protocol, setProtocol] = useState<Protocol | 'all'>('all')
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return integrations.filter((i) => {
      const protocolMatch =
        protocol === 'all' || i.protocols.includes(protocol)
      const queryMatch =
        q === '' ||
        i.name.toLowerCase().includes(q) ||
        i.category.toLowerCase().includes(q)
      return protocolMatch && queryMatch
    })
  }, [protocol, query])

  // Group by category for the rendered grid
  const grouped = useMemo(() => {
    return categories
      .map((cat) => ({
        category: cat,
        items: filtered.filter((i) => i.category === cat),
      }))
      .filter((g) => g.items.length > 0)
  }, [filtered])

  return (
    <Section tone="subtle" density="default" id="catalog">
      <Container size="xl">
        {/* Search + filter row */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-md">
            <SearchGlyph className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-tertiary" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search integrations (e.g. AWS, Fortigate, Zendesk)"
              className="w-full rounded-pill border border-line-subtle bg-surface py-3 pl-11 pr-4 text-body-sm text-ink-primary placeholder:text-ink-tertiary focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
              aria-label="Search integrations"
            />
          </div>
          <div className="-mx-2 overflow-x-auto lg:mx-0">
            <div role="tablist" aria-label="Protocol filters" className="inline-flex items-center gap-1 px-2 lg:px-0">
              {protocolFilters.map((f) => {
                const active = f.key === protocol
                return (
                  <button
                    key={f.key}
                    role="tab"
                    aria-selected={active}
                    onClick={() => setProtocol(f.key)}
                    className={`rounded-pill px-4 py-2 text-body-sm transition ${
                      active
                        ? 'bg-brand-900 text-white'
                        : 'bg-surface text-ink-secondary hover:bg-surface-muted'
                    }`}
                  >
                    {f.label}
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* Results count */}
        <div className="mt-6 text-body-sm text-ink-tertiary">
          {filtered.length} of {integrations.length} integrations
        </div>

        {/* Grouped grid */}
        {grouped.length === 0 ? (
          <div className="mt-16 rounded-3xl border border-dashed border-line bg-surface p-16 text-center">
            <Text size="md" tone="tertiary">
              No matches yet. Try a different keyword or clear the filter.
            </Text>
          </div>
        ) : (
          <div className="mt-10 space-y-14">
            {grouped.map((g) => (
              <div key={g.category}>
                <div className="flex items-baseline justify-between border-b border-line-subtle pb-3">
                  <h2 className="text-eyebrow font-semibold uppercase text-ink-tertiary">
                    {g.category}
                  </h2>
                  <span className="text-caption tabular-nums text-ink-muted">
                    {g.items.length}
                  </span>
                </div>
                <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {g.items.map((i, idx) => (
                    <IntegrationCard key={i.name} integration={i} index={idx} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Container>
    </Section>
  )
}

function IntegrationCard({
  integration,
  index,
}: {
  integration: Integration
  index: number
}) {
  const { locale } = useRouter()
  const cardCta = locale === 'tr' ? 'Entegrasyonu görüntüle' : 'View integration'
  return (
    <motion.a
      href={`/integrations/${integrationSlug(integration.name)}`}
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, ease: EASE, delay: (index % 6) * 0.04 }}
      className="group flex h-full flex-col justify-between rounded-2xl border border-line-subtle bg-surface p-5 transition duration-300 ease-smooth hover:-translate-y-0.5 hover:border-line hover:shadow-card-hover"
    >
      <div>
        <div className="flex items-start justify-between gap-3">
          <div className="text-body-md font-medium text-ink-primary">
            {integration.name}
          </div>
          {integration.highlight && (
            <span className="inline-flex items-center rounded-pill bg-accent-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent-700">
              Popular
            </span>
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {integration.protocols.map((p) => (
            <span
              key={p}
              className="inline-flex items-center rounded-pill border border-line-subtle bg-surface-subtle px-2 py-0.5 text-caption text-ink-tertiary"
            >
              {protocolLabels[p]}
            </span>
          ))}
        </div>
      </div>
      <div className="mt-5 inline-flex items-center gap-1.5 text-body-sm font-medium text-brand-900 transition group-hover:translate-x-0.5">
        {cardCta}
        <span aria-hidden>→</span>
      </div>
    </motion.a>
  )
}

function SearchGlyph({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none">
      <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

function ExternalGlyph({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none">
      <path d="M6 3h7v7M13 3 5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
